import React, { Component, Fragment } from 'react'
import Formsy from 'formsy-react'
import delay from 'lib/delay'

import Loader from 'components/UI/Loader/Loader'
import IconButton from 'components/UI/Button/IconButton'
import SensitiveInfoPopup from '../../../DashboardPopup/SensitiveInfoPopup/SensitiveInfoPopup'
import EmailErrorPopup from '../../../DashboardPopup/EmailErrorPopup/EmailErrorPopup'
import GeneralErrorPopup from '../../../DashboardPopup/GeneralErrorPopup/GeneralErrorPopup'
import SchoolDetailsColumn from './components/SchoolDetailsColumn/SchoolDetailsColumn'
import TeachersColumn from './components/TeachersColumn/TeachersColumn'
import VoucherOffer from '../Referrals/components/VoucherOffer/VoucherOffer'
import Blurb from '../Referrals/components/Blurb/Blurb'
import Ambassador from '../Referrals/components/Ambassador/Ambassador'
import Downloads from '../Referrals/components/Downloads/Downloads'
import {
  FormContent,
  ButtonColumn
} from './SchoolAccount.styles'
import { LoaderWrapper } from '../../DashboardSettings.styles'
import { colors, fonts } from 'modules/dashboard/Home/Dashboard.global.styles'

class SchoolAccount extends Component {
  constructor (props) {
    super(props)
    this.state = {
      initialState: {},
      schoolDetails: props.schoolDetails || undefined,
      teachers: props.teachers || [],
      subscriptionDetails: props.subscriptionDetails || {},
      isEditMode: false,
      ambassadorEmailSent: false
    }

    this.setSchoolState = this.setSchoolState.bind(this)
    this.setTeacherState = this.setTeacherState.bind(this)
    this.onAddTeacher = this.onAddTeacher.bind(this)
    this.onRemoveTeacher = this.onRemoveTeacher.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.sendAmbassadorEmail = this.sendAmbassadorEmail.bind(this)
  }

  UNSAFE_componentWillReceiveProps (nextProps) {
    // prevent state refresh after failed mutation (due to wrong password)
    const currentModalId = this.props.modal.getCurrent().id
    if (currentModalId === 'sensitiveInfoPopup' && this.state.schoolDetails) return

    const { schoolDetails, teachers, subscriptionDetails } = nextProps

    if (schoolDetails && teachers && subscriptionDetails) {
      this.setState({
        initialState: { schoolDetails, teachers },
        schoolDetails,
        teachers,
        subscriptionDetails
      })
    }
  }

  subscriptionStatus () {
    const { schoolDetails } = this.state
    const { subscription, subscriptionEndDate } = this.state.subscriptionDetails
    const now = (new Date()).getTime()

    if (schoolDetails && !schoolDetails._id && subscriptionEndDate >= now) return 'trial'
    if (subscription && subscription.current_period_end * 1000 >= now) return 'stripe'
    if (subscriptionEndDate >= now) return 'custom'
    return 'expired'
  }

  setSchoolState ({ field, value }) {
    this.setState({
      schoolDetails: {
        ...this.state.schoolDetails,
        [field]: value
      }
    })
  }

  setTeacherState ({ _id, field, value }) {
    const targetTeacher = this.state.teachers
      .find(teacher => teacher._id === _id)

    this.setState({
      teachers: this.state.teachers.map(teacher => (
        teacher._id === _id
          ? { ...targetTeacher, [field]: value }
			  : teacher
      ))
    })
  }

  onAddTeacher () {
    this.setState({
      teachers: [...this.state.teachers, {
        _id: Math.floor(Math.random() * 99999999),
        name: '',
        email: '',
        isNew: true
      }],
      isEditMode: true
    })
  }

  onRemoveTeacher (teacherId) {
    this.setState({
      teachers: this.state.teachers.reduce((result, teacher) => {
        if (teacher._id !== teacherId) {
          result.push(teacher)
        } else if (!teacher.isNew) {
          result.push({
            ...teacher,
            stagedForDeletion: true
          })
        }
        return result
      }, [])
    })
  }

  async onSubmit () {
    const changedSensitiveInfo = this.state.teachers
      .some(teacher => teacher.stagedForDeletion)

    if (changedSensitiveInfo) {
      return this.openSensitiveInfoModal()
    }

    try {
      await this.updateSchoolDetails()
      await this.addTeachers()
      this.setState({ isEditMode: false })
      await delay(1000)
      await this.refetchData()
    } catch (e) {
      this.props.modal.forceOpen(<GeneralErrorPopup isSettings />)
    }
  }

  onKeyPress (event) {
    if (event.which === 13 /* Enter */) {
      event.preventDefault()
    }
  }

  openSensitiveInfoModal () {
    const modal = this.props.modal

    modal.open(
      <SensitiveInfoPopup
        onConfirm={({ password }) => {
          Promise.all(
            this.state.teachers
              .filter(teacher => teacher.stagedForDeletion)
              .map(async teacher => {
                await this.props.deleteTeacher(
                  teacher._id,
                  this.state.schoolDetails._id,
                  password
                )
              }))
            .then(async () => {
              await this.updateSchoolDetails()
              await this.addTeachers()
              modal.close()
              this.setState({ isEditMode: false })
              await delay(1000)
              await this.refetchData()
            })
            .catch((e) => {
              if (e.graphQLErrors && e.graphQLErrors[0].message === 'Access Denied') {
                return modal.updateProps({
                  passwordError: true
                })
              }
              modal.forceOpen(<GeneralErrorPopup isSettings />)
            })
        }}
        onCancel={() => modal.close()}
      />,
      'sensitiveInfoPopup'
    )
  }

  async updateSchoolDetails () {
    if (this.subscriptionStatus() === 'trial') {
      await this.props.updateTrialSchoolDetails({
        schoolName: this.state.schoolDetails.name
      })
      return
    }

    await this.props.updateSchoolDetails({
      _id: this.state.schoolDetails._id,
      name: this.state.schoolDetails.name,
      twitter: this.state.schoolDetails.twitter
    })
  }

  async addTeachers () {
    const addTeacherResults = await Promise.all(
      this.state.teachers
        .filter(teacher => teacher.isNew)
        .map(async teacher => {
          try {
            await this.props.addTeacher(
              teacher.email,
              this.state.schoolDetails._id
            )
          } catch (e) {
            if (e.graphQLErrors && e.graphQLErrors[0].message === 'User Not Found') {
              return teacher.email
            }
            this.props.modal.forceOpen(<GeneralErrorPopup isSettings />)
          }
        }
        )
    )

    const notFoundErrors = addTeacherResults.filter(result => result)
    if (notFoundErrors.length) {
      this.props.modal.shyOpen(<EmailErrorPopup emails={notFoundErrors} />)
    }
  }

  async refetchData () {
    const { data } = await this.props.client.query({
      query: this.props.queries[0].query,
      fetchPolicy: 'network-only'
    })

    const school = data.me.school || {}

    const schoolDetails = {
      _id: school._id,
      name: school.name || data.me.schoolName,
      twitter: school.twitter
    }

    const teachers = school.teachers || [{
      _id: data.me._id,
      name: data.me.name,
      email: data.me.email
    }]

    this.setState({
      initialState: { schoolDetails, teachers },
      schoolDetails,
      teachers
    })
  }

  renderVoucherSection () {
    const teachers = this.state.initialState.teachers || []
    if (teachers.length < 6) {
      return (
        <Fragment>
          <VoucherOffer
            teachers={teachers}
            direction='darkLeft'
            ambassador={this.props.ambassador}
            sendAmbassadorEmail={this.sendAmbassadorEmail}
            ambassadorEmailSent={this.state.ambassadorEmailSent}
          />
          <Blurb direction='darkRight' />
          <Ambassador
            direction='darkLeft'
            ambassador={this.props.ambassador}
            sendAmbassadorEmail={this.sendAmbassadorEmail}
            ambassadorEmailSent={this.state.ambassadorEmailSent}
          />
          <Downloads direction='darkRight' />
        </Fragment>
      )
    }
    return null
  }

  sendAmbassadorEmail () {
    this.props.ambassadorSignUp().then(() => {
      this.setState({
        ambassadorEmailSent: true
      })
    })
  }

  render () {
    const { schoolDetails, teachers, isEditMode, subscriptionDetails } = this.state
    const { updateSchoolDetails, loading, loggedInUser } = this.props

    if (loading) {
      return (
        <LoaderWrapper>
          <Loader bgColor='rgba(0,0,0,0)' itemColor={colors.pink} itemSize='90' />
        </LoaderWrapper>
      )
    }

    return (
      <Formsy onKeyPress={this.onKeyPress} onValidSubmit={this.onSubmit}>
        <FormContent>
          <SchoolDetailsColumn
            subscriptionStatus={this.subscriptionStatus()}
            subscriptionEndDate={this.state.subscriptionDetails.subscriptionEndDate}
            isEditMode={isEditMode}
            info={schoolDetails}
            setSchoolState={this.setSchoolState}
          />
          {teachers &&
            [
              <TeachersColumn
                key='teachers-column'
                isEditMode={isEditMode}
                loggedInUser={loggedInUser}
                subscriptionStatus={this.subscriptionStatus()}
                allowedTeachers={subscriptionDetails.licence}
                teachers={teachers}
                onAddTeacher={this.onAddTeacher}
                onRemoveTeacher={this.onRemoveTeacher}
                setTeacherState={this.setTeacherState}
              />,
              <ButtonColumn key='button-column'>
                {this.state.isEditMode
                  ?	<Fragment>
                    <IconButton
                      bgColor={colors.lightBlue}
                      color='white'
                      iconName='cross'
                      size='regular'
                      onClick={() => this.setState({
                        ...this.state.initialState,
                        isEditMode: false
                      })}
                    >
                        Undo
                    </IconButton>
                    <IconButton
                      data-save
                      element='button'
                      type='submit'
                      bgColor={colors.grassGreen}
                      color='white'
                      iconName='check'
                      size='regular'
                    >
                        Save
                    </IconButton>
                  </Fragment>
                  : <IconButton
                    bgColor={colors.orange}
                    iconName='pencil'
                    color='white'
                    size='regular'
                    onClick={() => this.setState({
                      isEditMode: true
                    })}
                  >
                      Edit
                  </IconButton>
                }
              </ButtonColumn>
            ]
          }
        </FormContent>
        {this.renderVoucherSection()}
      </Formsy>
    )
  }
}

SchoolAccount.defaultProps = {
  modal: {
    open: () => {},
    close: () => {},
    closeAll: () => {}
  }
}

export default SchoolAccount
